import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { collection, writeBatch, doc, getDocs, query, orderBy, where } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { db } from '../../config/firebaseConfig';

const snapshotOf = (items) => JSON.stringify(items.map(({ value, isActive }) => ({ value, isActive })));

/**
 * One value list of Veri Girişi Değerleri (see valueLists.js): loading with
 * the defaults, editing (locked rows open with the pencil), active / passive,
 * adding (Enter on the last row adds one too), removing with the usage check
 * and saving — the Firestore writes of the old page. `label(key, vars)`
 * reads dataEntryValues.* texts.
 */
const useValueList = (config, label) => {
  const uidRef = useRef(0);
  const withUid = useCallback((item) => {
    uidRef.current += 1;
    return { uid: `v${uidRef.current}`, value: item.value ?? '', isActive: item.isActive !== false };
  }, []);

  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [checkingUid, setCheckingUid] = useState(null);
  const [editing, setEditing] = useState({});
  const [errors, setErrors] = useState({});
  const [saved, setSaved] = useState('[]');

  const name = label(`lists.${config.key}.name`);
  const listRef = useMemo(() => collection(db, ...config.path), [config.path]);

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      const querySnapshot = await getDocs(query(listRef, orderBy('id', 'asc')));
      const fetched = querySnapshot.docs.map((d) => {
        const data = d.data();
        return { value: data.value, isActive: data.isActive !== false }; // active unless stated
      });
      const next = (fetched.length ? fetched : config.defaults).map(withUid);
      setItems(next);
      setSaved(snapshotOf(fetched.length ? next : []));
    } catch (error) {
      console.error(`Error fetching ${config.key}:`, error);
      toast.error(label('toast.loadError', { name }));
    } finally {
      setIsLoading(false);
    }
  }, [listRef, config, withUid, label, name]);

  useEffect(() => {
    load();
    // Yalnızca açılışta
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearError = (uid) => setErrors((current) => {
    if (!current[uid]) return current;
    const next = { ...current };
    delete next[uid];
    return next;
  });

  const updateValue = (uid, value) => {
    setItems((current) => current.map((item) => (item.uid === uid ? { ...item, value } : item)));
    clearError(uid);
  };

  const toggleActive = (uid) => setItems((current) => current.map((item) => (item.uid === uid ? { ...item, isActive: !item.isActive } : item)));
  const toggleEdit = (uid) => setEditing((current) => ({ ...current, [uid]: !current[uid] }));

  const addItem = () => {
    const item = withUid({ value: '', isActive: true });
    setItems((current) => [...current, item]);
    // A new row opens for typing
    if (config.lockable) setEditing((current) => ({ ...current, [item.uid]: true }));
    return item.uid;
  };

  /** Duplicate values (trimmed, empties ignored) get an error each. */
  const validate = (list) => {
    const seen = new Map();
    const next = {};
    list.forEach((item) => {
      const trimmed = String(item.value).trim();
      if (!trimmed) return;
      if (seen.has(trimmed)) next[item.uid] = label('validation.duplicate');
      else seen.set(trimmed, item.uid);
    });
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const save = async (list = items) => {
    if (config.mode === 'indexed') {
      if (!validate(list)) {
        toast.error(label('toast.checkValues'));
        return false;
      }
    }
    try {
      setIsSaving(true);
      const batch = writeBatch(db);
      // Delete the existing documents first
      const existingDocsSnapshot = await getDocs(listRef);
      existingDocsSnapshot.forEach((d) => batch.delete(d.ref));

      if (config.mode === 'indexed') {
        // Empty rows are not saved; ids follow the order
        list.filter((item) => String(item.value).trim() !== '').forEach((item, index) => {
          batch.set(doc(listRef, index.toString()), { id: index, value: String(item.value).trim(), isActive: item.isActive });
        });
      } else {
        list.forEach((item, index) => {
          batch.set(doc(listRef), { id: index + 1, value: item.value, isActive: item.isActive });
        });
      }

      await batch.commit();
      toast.success(label('toast.saved', { name }));
      if (config.mode === 'indexed') {
        await load();
        setEditing({});
      } else {
        setSaved(snapshotOf(list));
      }
      return true;
    } catch (error) {
      console.error(`Error saving ${config.key}:`, error);
      toast.error(label('toast.saveError', { name, message: error.message }));
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  /** Is the value used by records (employees, users, budgets)? */
  const isInUse = async (value) => {
    if (!config.reference) return false;
    try {
      const querySnapshot = await getDocs(query(collection(db, config.reference.collection), where(config.reference.field, '==', value)));
      return !querySnapshot.empty;
    } catch (error) {
      console.error(`Error checking ${config.key} references:`, error);
      toast.error(label('toast.checkError', { name }));
      return true; // treat as used, to be safe
    }
  };

  const removeLocal = (uid, list = items) => {
    const next = list.filter((item) => item.uid !== uid);
    // At least one row stays
    return next.length === 0 ? [withUid({ value: '', isActive: true })] : next;
  };

  /**
   * Remove pressed. Expense lists and empty rows go at once; otherwise the
   * usage is checked and the page is asked to confirm 'deactivate' (in use)
   * or 'delete'. Returns that request, or null.
   */
  const requestRemove = async (uid) => {
    const item = items.find((entry) => entry.uid === uid);
    if (!item) return null;
    if (config.mode === 'auto') {
      setItems((current) => current.filter((entry) => entry.uid !== uid));
      return null;
    }
    if (String(item.value).trim() === '') {
      setItems((current) => removeLocal(uid, current));
      return null;
    }
    setCheckingUid(uid);
    const inUse = await isInUse(item.value);
    setCheckingUid(null);
    return { type: inUse ? 'deactivate' : 'delete', uid, value: item.value };
  };

  const confirmDeactivate = (uid) => {
    setItems((current) => current.map((item) => (item.uid === uid ? { ...item, isActive: false } : item)));
    toast.info(label(`lists.${config.key}.deactivated`));
  };

  /** Deletes the row and saves the list straight away (as before). */
  const confirmDelete = async (uid) => {
    const next = removeLocal(uid);
    setItems(next);
    toast.success(label(`lists.${config.key}.deleted`));
    return save(next);
  };

  const dirty = !isLoading && snapshotOf(items.filter((item) => config.mode === 'auto' || String(item.value).trim() !== '')) !== saved;
  const counts = useMemo(() => ({
    active: items.filter((item) => item.isActive && String(item.value).trim()).length,
    passive: items.filter((item) => !item.isActive && String(item.value).trim()).length,
  }), [items]);

  return {
    config,
    items,
    isLoading,
    isSaving,
    checkingUid,
    editing,
    errors,
    dirty,
    counts,
    updateValue,
    toggleActive,
    toggleEdit,
    addItem,
    save,
    requestRemove,
    confirmDeactivate,
    confirmDelete,
  };
};

export default useValueList;
